/**
 * Subcontractor entity DTOs.
 *
 * Mirrors Prisma `Subcontractor` (prisma/schema.prisma:1016) and the
 * `SubcontractorProject` link (prisma/schema.prisma:1049). Subcontractors
 * are account-level (one per user, reusable across projects); linking a
 * subcontractor to a project creates a SubcontractorProject row that
 * carries project-specific scope notes.
 *
 * Ids are cuid() per the Prisma model for real rows, but validated with the
 * shared `entityIdSchema` (non-empty opaque string) so demo/seed tenants'
 * readable `seed-*` ids parse too. That includes `SubcontractorProjectLink.id`:
 * prisma/seed-demo-compliance.ts writes readable link ids, and a `.cuid()`
 * here made the strict response parse 500 every list request for a project
 * holding one (staging, Oct 2026).
 */
import { z } from "zod";
import { entityIdSchema } from "../common";

export const SUBCONTRACTOR_CLASSIFICATIONS = [
  "CONTRACTOR",
  "SUBCONTRACTOR",
  "SUPPLIER",
  "CONSULTANT",
] as const;

export type SubcontractorClassification =
  (typeof SUBCONTRACTOR_CLASSIFICATIONS)[number];

export const SubcontractorClassificationSchema = z.enum(
  SUBCONTRACTOR_CLASSIFICATIONS,
);

/**
 * Persisted project-link classifications include WORKER, which the web app
 * uses for people working under the Principal Contractor's direction. Mobile
 * can read that value, but link/create requests intentionally retain the
 * narrower `SubcontractorClassificationSchema` until they implement the
 * worker-creation side effect.
 */
export const SUBCONTRACTOR_PROJECT_CLASSIFICATIONS = [
  ...SUBCONTRACTOR_CLASSIFICATIONS,
  "WORKER",
] as const;

export type SubcontractorProjectClassification =
  (typeof SUBCONTRACTOR_PROJECT_CLASSIFICATIONS)[number];

export const SubcontractorProjectClassificationSchema = z.enum(
  SUBCONTRACTOR_PROJECT_CLASSIFICATIONS,
);

export const SubcontractorSchema = z
  .object({
    id: entityIdSchema,
    userId: z.string(),
    companyName: z.string(),
    contactName: z.string().nullable(),
    contactFirstName: z.string().nullable(),
    contactLastName: z.string().nullable(),
    contactEmail: z.string().nullable(),
    contactPhone: z.string().nullable(),
    insuranceFileUrl: z.string().nullable(),
    insuranceExpiresAt: z.string().datetime().nullable(),
    ramsFileUrl: z.string().nullable(),
    ramsExpiresAt: z.string().datetime().nullable(),
    liabilityFileUrl: z.string().nullable(),
    liabilityExpiresAt: z.string().datetime().nullable(),
    hasFirstAider: z.boolean(),
    hasFireWarden: z.boolean(),
    notes: z.string().nullable(),
    hsPolicyUrl: z.string().nullable(),
    hsPolicyUploadedAt: z.string().datetime().nullable(),
    competencyNotes: z.string().nullable(),
    previousWorkRefs: z.string().nullable(),
    scopeOfWork: z.string().nullable(),
    tradeSpecialities: z.array(z.string()),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  });

export type Subcontractor = z.infer<typeof SubcontractorSchema>;

/**
 * Project-scoped link row. Returned by GET
 * /api/mobile/v1/projects/:projectId/subcontractors as part of each entry.
 */
export const SubcontractorProjectLinkSchema = z
  .object({
    id: entityIdSchema,
    subcontractorId: entityIdSchema,
    projectId: entityIdSchema,
    classification: SubcontractorProjectClassificationSchema,
    scopeOnProject: z.string().nullable(),
    supervisionContact: z.string().nullable(),
    cppContribution: z.string().nullable(),
    projectRamsRef: z.string().nullable(),
    workerRecordCreated: z.boolean(),
    createdAt: z.string().datetime(),
  });

export type SubcontractorProjectLink = z.infer<
  typeof SubcontractorProjectLinkSchema
>;

/**
 * Combined shape returned in list views — the account-level subcontractor
 * card plus the per-project link metadata.
 */
export const SubcontractorOnProjectSchema = z.object({
  subcontractor: SubcontractorSchema,
  link: SubcontractorProjectLinkSchema,
});

export type SubcontractorOnProject = z.infer<
  typeof SubcontractorOnProjectSchema
>;

// ─── #2411 server-boundary variants ─────────────────────────────────────────
// The mobile app parses the permissive bases above; the routes serialise
// through these Strict forms (full depth — the composite re-states both the
// subcontractor card and the per-project link).

export const SubcontractorStrict = SubcontractorSchema.strict();
export const SubcontractorProjectLinkStrict =
  SubcontractorProjectLinkSchema.strict();

export const SubcontractorOnProjectStrict = SubcontractorOnProjectSchema.extend(
  {
    subcontractor: SubcontractorStrict,
    link: SubcontractorProjectLinkStrict,
  },
).strict();

export const SubcontractorCreateRequest = z
  .object({
    companyName: z.string().min(1),
    contactName: z.string().nullable().optional(),
    contactFirstName: z.string().nullable().optional(),
    contactLastName: z.string().nullable().optional(),
    contactEmail: z.string().nullable().optional(),
    contactPhone: z.string().nullable().optional(),
    insuranceFileUrl: z.string().nullable().optional(),
    insuranceExpiresAt: z.string().datetime().nullable().optional(),
    ramsFileUrl: z.string().nullable().optional(),
    ramsExpiresAt: z.string().datetime().nullable().optional(),
    liabilityFileUrl: z.string().nullable().optional(),
    liabilityExpiresAt: z.string().datetime().nullable().optional(),
    hasFirstAider: z.boolean().default(false),
    hasFireWarden: z.boolean().default(false),
    notes: z.string().nullable().optional(),
    hsPolicyUrl: z.string().nullable().optional(),
    competencyNotes: z.string().nullable().optional(),
    previousWorkRefs: z.string().nullable().optional(),
    scopeOfWork: z.string().nullable().optional(),
    tradeSpecialities: z.array(z.string()).default([]),
  })
  .strict();

export type SubcontractorCreateRequest = z.infer<
  typeof SubcontractorCreateRequest
>;

export const SubcontractorUpdateRequest = SubcontractorCreateRequest.partial();

export type SubcontractorUpdateRequest = z.infer<
  typeof SubcontractorUpdateRequest
>;

/**
 * POST /api/mobile/v1/projects/:projectId/subcontractors body. Links an
 * EXISTING account subcontractor to the project (`subcontractorId`), or —
 * exactly-one-of — creates a new account firm and links it in the same
 * server transaction (`createSubcontractor`). The atomic compose exists so
 * the inline "add a new firm" path can't strand an unlinked duplicate firm
 * when the second request of a two-step flow fails (a Starter can't even
 * SEE the orphan — the directory GET is Pro-gated).
 */
export const SubcontractorLinkToProjectRequest = z
  .object({
    subcontractorId: entityIdSchema.optional(),
    createSubcontractor: z
      .object({ companyName: z.string().min(1) })
      .strict()
      .optional(),
    classification: SubcontractorClassificationSchema.default("SUBCONTRACTOR"),
    scopeOnProject: z.string().nullable().optional(),
    supervisionContact: z.string().nullable().optional(),
    cppContribution: z.string().nullable().optional(),
    projectRamsRef: z.string().nullable().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    const picks =
      (value.subcontractorId ? 1 : 0) + (value.createSubcontractor ? 1 : 0);
    if (picks !== 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          "Provide exactly one of subcontractorId or createSubcontractor.",
        path: ["subcontractorId"],
      });
    }
  });

export type SubcontractorLinkToProjectRequest = z.infer<
  typeof SubcontractorLinkToProjectRequest
>;
